import { useState, useEffect } from 'react';
import { Layout } from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';
import {
  RefreshCw,
  Download,
  Upload,
  AlertTriangle,
  CheckCircle,
  Loader2,
  ArrowRightLeft,
  Database,
  Cloud,
  ChevronDown,
  ChevronRight,
  X
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function DiffBadge({ count, label, color }) {
  return (
    <div className={`p-4 border ${color} text-center`}>
      <p className="text-3xl font-bold font-mono">{count}</p>
      <p className="text-xs text-muted-foreground mt-1 uppercase tracking-wider">{label}</p>
    </div>
  );
}

function ConfirmDialog({ open, title, message, warning, details, onConfirm, onCancel, loading, confirmLabel }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onCancel}>
      <div className="bg-card border border-border w-full max-w-lg mx-4 max-h-[80vh] flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between p-4 border-b border-border">
          <h3 className="font-['Oswald'] text-lg font-bold uppercase">{title}</h3>
          <button onClick={onCancel}><X size={20} className="text-muted-foreground" /></button>
        </div>
        <div className="p-4 overflow-y-auto flex-1 space-y-3">
          {warning && (
            <div className="flex items-start gap-2 p-3 bg-yellow-500/10 border border-yellow-500/30">
              <AlertTriangle size={18} className="text-yellow-500 flex-shrink-0 mt-0.5" />
              <p className="text-sm">{warning}</p>
            </div>
          )}
          <p className="text-sm text-foreground/80">{message}</p>
          {details && details.length > 0 && (
            <div className="max-h-48 overflow-y-auto border border-border bg-secondary/30 p-3 space-y-1">
              {details.map((d, i) => (
                <p key={i} className="text-xs font-mono">{d}</p>
              ))}
            </div>
          )}
        </div>
        <div className="flex gap-3 p-4 border-t border-border">
          <Button variant="outline" onClick={onCancel} className="flex-1 rounded-none" disabled={loading}>
            Abbrechen
          </Button>
          <Button onClick={onConfirm} disabled={loading} className="flex-1 bg-primary hover:bg-red-700 text-white rounded-none" data-testid="confirm-sync-btn">
            {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            {confirmLabel || 'Bestätigen'}
          </Button>
        </div>
      </div>
    </div>
  );
}

export default function AdminSyncPage() {
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [diff, setDiff] = useState(null);
  const [showImportConfirm, setShowImportConfirm] = useState(false);
  const [showExportConfirm, setShowExportConfirm] = useState(false);
  const [expandedDiffs, setExpandedDiffs] = useState([]);
  const [lastResult, setLastResult] = useState(null);

  const getToken = () => localStorage.getItem('shrt_token');

  const fetchDiff = async () => {
    setLoading(true);
    setLastResult(null);
    try {
      const res = await axios.get(`${API_URL}/api/sync/monday/diff`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      setDiff(res.data);
    } catch (err) {
      toast.error('Fehler beim Laden der Differenzen');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchDiff(); }, []);

  const handleImport = async () => {
    setSyncing(true);
    try {
      const res = await axios.post(`${API_URL}/api/sync/monday/import-new`, {}, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      setLastResult({ type: 'import', data: res.data });
      toast.success(res.data.message);
      setShowImportConfirm(false);
      fetchDiff();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Import fehlgeschlagen');
    } finally {
      setSyncing(false);
    }
  };

  const handleExport = async () => {
    setSyncing(true);
    try {
      const res = await axios.post(`${API_URL}/api/sync/monday/export-to-monday`, {}, {
        headers: { Authorization: `Bearer ${getToken()}` },
        timeout: 120000
      });
      setLastResult({ type: 'export', data: res.data });
      toast.success(res.data.message);
      setShowExportConfirm(false);
      fetchDiff();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Export fehlgeschlagen');
    } finally {
      setSyncing(false);
    }
  };

  const toggleDiffExpand = (name) => {
    setExpandedDiffs(prev => prev.includes(name) ? prev.filter(n => n !== name) : [...prev, name]);
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <ArrowRightLeft className="text-primary" size={28} />
            <h1 className="font-['Oswald'] text-2xl font-bold text-foreground uppercase tracking-tight">
              Synchronisation
            </h1>
          </div>
          <Button variant="outline" onClick={fetchDiff} disabled={loading} className="rounded-none" data-testid="refresh-diff-btn">
            <RefreshCw size={16} className={`mr-2 ${loading ? 'animate-spin' : ''}`} />
            Aktualisieren
          </Button>
        </div>

        {/* Info Banner */}
        <div className="bg-blue-500/10 border border-blue-500/30 p-4 flex items-start gap-3">
          <Database size={18} className="text-blue-400 flex-shrink-0 mt-0.5" />
          <div className="text-sm">
            <strong>Die App ist die Hauptquelle.</strong> Monday.com dient als Backup.
            Beim Export werden App-Daten nach Monday geschrieben. Beim Import werden nur neue Einträge von Monday geholt.
          </div>
        </div>

        {/* Overview Cards */}
        {diff && (
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
            <DiffBadge count={diff.app_count} label="In App" color="border-blue-500/30 bg-blue-500/5" />
            <DiffBadge count={diff.monday_count} label="Auf Monday" color="border-orange-500/30 bg-orange-500/5" />
            <DiffBadge count={diff.only_in_app_count} label="Nur in App" color={diff.only_in_app_count > 0 ? 'border-yellow-500/30 bg-yellow-500/5' : 'border-border'} />
            <DiffBadge count={diff.only_on_monday_count} label="Nur Monday" color={diff.only_on_monday_count > 0 ? 'border-yellow-500/30 bg-yellow-500/5' : 'border-border'} />
            <DiffBadge count={diff.field_diff_count} label="Abweichungen" color={diff.field_diff_count > 0 ? 'border-orange-500/30 bg-orange-500/5' : 'border-border'} />
          </div>
        )}

        {/* Action Buttons */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="bg-card border border-border p-5">
            <div className="flex items-center gap-2 mb-2">
              <Download size={18} className="text-blue-400" />
              <h3 className="font-['Oswald'] font-bold uppercase text-sm tracking-wider">Import von Monday</h3>
            </div>
            <p className="text-xs text-muted-foreground mb-4">
              Nur neue Mitglieder von Monday.com in die App importieren. Bestehende Einträge werden nicht verändert.
            </p>
            <Button
              onClick={() => setShowImportConfirm(true)}
              disabled={syncing || !diff || diff.only_on_monday_count === 0}
              variant="outline"
              className="rounded-none w-full"
              data-testid="import-btn"
            >
              <Download size={16} className="mr-2" />
              {diff?.only_on_monday_count || 0} neue importieren
            </Button>
          </div>

          <div className="bg-card border border-border p-5">
            <div className="flex items-center gap-2 mb-2">
              <Upload size={18} className="text-green-400" />
              <h3 className="font-['Oswald'] font-bold uppercase text-sm tracking-wider">Export nach Monday</h3>
            </div>
            <p className="text-xs text-muted-foreground mb-4">
              App-Daten nach Monday.com schreiben. Neue Einträge werden erstellt, bestehende aktualisiert (ohne Fahrzeuge).
            </p>
            <Button
              onClick={() => setShowExportConfirm(true)}
              disabled={syncing}
              className="bg-primary hover:bg-red-700 text-white rounded-none w-full"
              data-testid="export-btn"
            >
              <Upload size={16} className="mr-2" />
              Nach Monday exportieren
            </Button>
          </div>
        </div>

        {/* Last Result */}
        {lastResult && (
          <div className="bg-green-500/10 border border-green-500/30 p-4">
            <div className="flex items-center gap-2 mb-2">
              <CheckCircle size={18} className="text-green-500" />
              <span className="font-bold text-sm">{lastResult.type === 'import' ? 'Import' : 'Export'} abgeschlossen</span>
            </div>
            <p className="text-sm">{lastResult.data.message}</p>
            {lastResult.data.added_names?.length > 0 && (
              <div className="mt-2 text-xs text-muted-foreground">
                Importiert: {lastResult.data.added_names.join(', ')}
              </div>
            )}
          </div>
        )}

        {/* Only in App */}
        {diff?.only_in_app?.length > 0 && (
          <section className="bg-card border border-border p-4 sm:p-6">
            <h2 className="font-['Oswald'] text-sm font-bold uppercase tracking-wider mb-3 flex items-center gap-2">
              <Database size={16} className="text-blue-400" />
              Nur in App ({diff.only_in_app.length})
            </h2>
            <div className="space-y-1">
              {diff.only_in_app.map((m, i) => (
                <div key={i} className="flex items-center justify-between py-1.5 border-b border-border/50 last:border-0 text-sm">
                  <div>
                    <span className="font-medium">{m.name}</span>
                    <span className="text-muted-foreground ml-2 text-xs">{m.email}</span>
                  </div>
                  {m.status === 'ausgetreten' && (
                    <span className="text-xs px-2 py-0.5 bg-red-500/20 text-red-400">ausgetreten</span>
                  )}
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Only on Monday */}
        {diff?.only_on_monday?.length > 0 && (
          <section className="bg-card border border-border p-4 sm:p-6">
            <h2 className="font-['Oswald'] text-sm font-bold uppercase tracking-wider mb-3 flex items-center gap-2">
              <Cloud size={16} className="text-orange-400" />
              Nur auf Monday.com ({diff.only_on_monday.length})
            </h2>
            <div className="space-y-1">
              {diff.only_on_monday.map((m, i) => (
                <div key={i} className="py-1.5 border-b border-border/50 last:border-0 text-sm">
                  <span className="font-medium">{m.name}</span>
                  <span className="text-muted-foreground ml-2 text-xs">{m.email || 'keine E-Mail'}</span>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Field Differences */}
        {diff?.field_differences?.length > 0 && (
          <section className="bg-card border border-border p-4 sm:p-6">
            <h2 className="font-['Oswald'] text-sm font-bold uppercase tracking-wider mb-3 flex items-center gap-2">
              <AlertTriangle size={16} className="text-orange-400" />
              Feld-Abweichungen ({diff.field_differences.length})
            </h2>
            <p className="text-xs text-muted-foreground mb-3">
              Diese Mitglieder existieren in beiden Systemen, haben aber unterschiedliche Werte. Beim Export werden die App-Werte nach Monday geschrieben.
            </p>
            <div className="space-y-1">
              {diff.field_differences.map((m, i) => {
                const isExpanded = expandedDiffs.includes(m.name);
                return (
                  <div key={i} className="border border-border/50">
                    <button onClick={() => toggleDiffExpand(m.name)} className="w-full flex items-center justify-between p-2.5 hover:bg-muted/30 text-left">
                      <div className="flex items-center gap-2">
                        {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                        <span className="font-medium text-sm">{m.name}</span>
                        <span className="text-xs text-muted-foreground">{m.differences.length} Unterschied{m.differences.length > 1 ? 'e' : ''}</span>
                      </div>
                    </button>
                    {isExpanded && (
                      <div className="px-3 pb-3 border-t border-border/50 overflow-x-auto">
                        <table className="w-full text-xs mt-2">
                          <thead>
                            <tr className="text-muted-foreground">
                              <th className="text-left py-1 font-medium">Feld</th>
                              <th className="text-left py-1 font-medium">App (Hauptquelle)</th>
                              <th className="text-left py-1 font-medium">Monday.com</th>
                            </tr>
                          </thead>
                          <tbody>
                            {m.differences.map((d, j) => (
                              <tr key={j} className="border-t border-border/30">
                                <td className="py-1.5 font-medium">{d.field}</td>
                                <td className="py-1.5 text-blue-400">{d.app_value || '—'}</td>
                                <td className="py-1.5 text-orange-400">{d.monday_value || '—'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {/* All synced */}
        {diff && diff.only_in_app_count === 0 && diff.only_on_monday_count === 0 && diff.field_diff_count === 0 && (
          <div className="bg-green-500/10 border border-green-500/30 p-6 text-center">
            <CheckCircle size={32} className="text-green-500 mx-auto mb-2" />
            <p className="font-bold">Alles synchron</p>
            <p className="text-sm text-muted-foreground">Keine Differenzen zwischen App und Monday.com</p>
          </div>
        )}
      </div>

      {/* Import Confirmation Dialog */}
      <ConfirmDialog
        open={showImportConfirm}
        title="Import von Monday.com"
        warning="Es werden NUR neue Mitglieder importiert. Bestehende Einträge in der App werden nicht verändert."
        message={`${diff?.only_on_monday_count || 0} neue Mitglieder von Monday.com importieren?`}
        details={diff?.only_on_monday?.map(m => `+ ${m.name} (${m.email || 'keine E-Mail'})`)}
        onConfirm={handleImport}
        onCancel={() => setShowImportConfirm(false)}
        loading={syncing}
        confirmLabel="Importieren"
      />

      {/* Export Confirmation Dialog */}
      <ConfirmDialog
        open={showExportConfirm}
        title="Export nach Monday.com"
        warning="Die App ist die Hauptquelle. Alle App-Daten (ausser Fahrzeuge) werden nach Monday.com geschrieben und bestehende Monday-Einträge überschrieben."
        message={`${diff?.app_count || 0} Mitglieder nach Monday.com exportieren? (${diff?.only_in_app_count || 0} neu, ${diff?.app_count - diff?.only_in_app_count || 0} aktualisieren, ${diff?.field_diff_count || 0} Abweichungen korrigieren)`}
        details={[
          ...(diff?.only_in_app?.map(m => `NEU: ${m.name} (${m.email})`) || []),
          ...(diff?.field_differences?.map(m => `UPDATE: ${m.name} (${m.differences.length} Felder)`) || [])
        ]}
        onConfirm={handleExport}
        onCancel={() => setShowExportConfirm(false)}
        loading={syncing}
        confirmLabel="Exportieren"
      />
    </Layout>
  );
}
